import { useEffect, useState } from "react";
import PageSeo from "@/components/seo/PageSeo";
import SeoImage from "@/components/seo/SeoImage";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { API } from "@/App";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Clock, Eye, User } from "lucide-react";

const BlogDetailPage = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [internalLinks, setInternalLinks] = useState([]);

  useEffect(() => {
    const fetchBlogAndLinks = async () => {
      try {
        const [blogRes, linksRes] = await Promise.all([
          axios.get(`${API}/blogs/${slug}`),
          axios.get(`${API}/seo/internal-links`).catch(() => ({ data: [] }))
        ]);
        setBlog(blogRes.data);
        setInternalLinks(linksRes.data);
      } catch (err) {
        console.error("Error fetching blog:", err); // eslint-disable-line no-console
      } finally {
        setLoading(false);
      }
    };
    fetchBlogAndLinks();
  }, [slug]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const renderBlogContent = (content) => {
    if (!content) return '';
    const isHtml = /<[a-z][\s\S]*>/i.test(content);
    let html = isHtml
      ? content
      : content
          .replace(/^### (.+)$/gm, '<h3 class="text-xl font-bold text-[#00FF88] font-outfit mt-6 mb-2">$1</h3>')
          .replace(/^## (.+)$/gm, '<h2 class="text-2xl font-bold text-white font-outfit mt-8 mb-3 pb-2 border-b border-white/10">$1</h2>')
          .replace(/\*\*(.+?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>')
          .replace(/\n\n/g, '</p><p class="leading-relaxed mb-4">')
          .replace(/\n/g, '<br/>');

    // Auto-inject internal links
    internalLinks.forEach(link => {
      const regex = new RegExp(`\\b(${link.keyword})\\b(?![^<]*>|[^<>]*<\/a>)`, 'gi');
      html = html.replace(regex, `<a href="${link.target_url}" class="text-[#00F0FF] hover:underline hover:text-[#00FF88] transition-colors font-medium">$1</a>`);
    });

    return html;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f1419] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#00d991] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-[#0f1419] flex flex-col items-center justify-center gap-4">
        <p className="text-[#6b7280]">Blog post not found</p>
        <Link to="/blog"><Button variant="ghost" className="text-[#00d991]"><ArrowLeft className="w-4 h-4 mr-2" /> Back to Blog</Button></Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f1419]" data-testid="blog-detail-page">
      <PageSeo path={`/blog/${slug}`} fallbackTitle={blog?.title || "Blog"} fallbackDescription={blog?.excerpt || ""} />
      {/* Nav */}
      <nav className="sticky top-0 z-50 px-6 py-4 bg-[#0f1419]/90 backdrop-blur-xl border-b border-[#2a3142]">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded overflow-hidden shadow-[0_0_20px_rgba(0,255,136,0.3)]">
              <img src="/jamadhan-logo.png" alt="Jamadhan" className="w-full h-full object-cover" />
            </div>
            <span className="text-xl font-bold text-white font-outfit tracking-tight">JAMADHAN</span>
          </Link>
          <Link to="/blog">
            <Button variant="ghost" className="text-[#b0b8c4] hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" /> All Posts
            </Button>
          </Link>
        </div>
      </nav>

      {/* Cover */}
      {blog.cover_image && (
        <div className="relative w-full h-64 md:h-80 overflow-hidden">
          <SeoImage src={blog.cover_image} alt={blog.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0f1419] to-transparent" />
        </div>
      )}

      {/* Content */}
      <article className="max-w-3xl mx-auto px-6 py-12" data-testid="blog-content">
        <div className="flex flex-wrap gap-1.5 mb-4">
          {(blog.tags || []).map((tag, i) => (
            <Badge key={tag} className="bg-[#00d991]/10 text-[#00d991] border-0 text-xs">{tag}</Badge>
          ))}
        </div>

        <h1 className="text-3xl sm:text-4xl font-bold text-white font-outfit mb-4" data-testid="blog-post-title">
          {blog.title}
        </h1>

        <div className="flex items-center gap-4 text-sm text-[#6b7280] mb-8 pb-8 border-b border-[#2a3142]">
          <span className="flex items-center gap-1"><User className="w-4 h-4" />{blog.author_name}</span>
          <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{formatDate(blog.created_at)}</span>
          <span className="flex items-center gap-1"><Eye className="w-4 h-4" />{blog.views} views</span>
        </div>

        <div
          className="prose prose-invert max-w-none text-[#CBD5E1] text-base leading-relaxed space-y-4 font-plex blog-content-view"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(renderBlogContent(blog.content))
          }}
        />

        <style>{`
          .blog-content-view h1 { font-family: Outfit, sans-serif; font-size: 2rem; font-weight: 800; color: #fff; margin-top: 2rem; margin-bottom: 1rem; }
          .blog-content-view h2 { font-family: Outfit, sans-serif; font-size: 1.6rem; font-weight: 800; color: #fff; margin-top: 1.75rem; margin-bottom: 0.75rem; border-bottom: 1px solid rgba(255,255,255,0.1); padding-bottom: 0.5rem; }
          .blog-content-view h3 { font-family: Outfit, sans-serif; font-size: 1.3rem; font-weight: 700; color: #00FF88; margin-top: 1.5rem; margin-bottom: 0.5rem; }
          .blog-content-view h4 { font-family: Outfit, sans-serif; font-size: 1.1rem; font-weight: 700; color: #E2E8F0; margin-top: 1.25rem; margin-bottom: 0.5rem; }
          .blog-content-view p { color: #CBD5E1; line-height: 1.8; margin-bottom: 1rem; }
          .blog-content-view table { width: 100%; border-collapse: collapse; margin: 1.5rem 0; font-size: 0.95rem; border-radius: 12px; overflow: hidden; border: 1px solid rgba(255,255,255,0.12); }
          .blog-content-view th { background: rgba(0,255,136,0.12); color: #00FF88; font-family: Outfit, sans-serif; font-weight: 700; padding: 12px 16px; border: 1px solid rgba(255,255,255,0.15); text-align: left; }
          .blog-content-view td { padding: 12px 16px; border: 1px solid rgba(255,255,255,0.08); color: #CBD5E1; background: rgba(255,255,255,0.02); }
          .blog-content-view tr:nth-child(even) td { background: rgba(255,255,255,0.04); }
          .blog-content-view ul { list-style-type: disc; padding-left: 1.5rem; margin-bottom: 1rem; color: #CBD5E1; }
          .blog-content-view ol { list-style-type: decimal; padding-left: 1.5rem; margin-bottom: 1rem; color: #CBD5E1; }
          .blog-content-view li { margin-bottom: 0.4rem; line-height: 1.7; }
          .blog-content-view blockquote { border-left: 3px solid #00FF88; padding-left: 1rem; color: #94A3B8; font-style: italic; margin: 1.5rem 0; }
        `}</style>

        <div className="mt-12 pt-8 border-t border-[#2a3142] text-center">
          <Link to="/blog">
            <Button className="bg-[#00d991] text-black font-semibold hover:bg-[#00ff9f]">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to All Posts
            </Button>
          </Link>
        </div>
      </article>

      {/* Footer */}
      <footer className="border-t border-[#2a3142] py-8 px-6">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded overflow-hidden shadow-[0_0_18px_rgba(0,255,136,0.28)]">
              <img src="/jamadhan-logo.png" alt="Jamadhan" className="w-full h-full object-cover" />
            </div>
            <span className="text-white font-semibold font-outfit tracking-tight">JAMADHAN</span>
          </div>
          <p className="text-sm text-[#6b7280]">&copy; {new Date().getFullYear()} Jamadhan. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default BlogDetailPage;
